 import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import esES from 'date-fns/locale/es';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, MenuItem, TextField, Grid, Alert, Box, Typography, Chip, Divider, Tooltip } from '@mui/material';

// Paleta de colores fuera del componente
const COLORS = [
  '#348125ff', // Rojo fuerte
  '#0e4f7aff', // Azul intenso
  '#0d5c2eff', // Verde puro
  '#b3720bff', // Naranja vibrante
  '#093b3bff', // Púrpura
  '#b10979ff', // Verde azulado
  '#20323dff', // Naranja oscuro
  '#3b0f8dff', // Azul petróleo
  '#E67E22', // Mandarina
  '#df3f39ff', // Turquesa claro
  '#bd417fff', // Rojo oscuro
  '#353864ff', // Azul grisáceo
  '#0d6e75ff', // Amarillo puro
  '#6b1cb4ff', // Gris medio
  '#fd0fb6ff'  // Verde claro
];

// Función para asignar color según la referencia
function getColorForReferencia(referencia) {
  if (!referencia) return COLORS[0];
  // Normaliza: quita espacios y pasa a minúsculas
  let refStr = String(referencia).trim().toLowerCase();
  let hash = 0;
  for (let i = 0; i < refStr.length; i++) {
    hash = refStr.charCodeAt(i) + ((hash << 5) - hash);
  }
  return COLORS[Math.abs(hash) % COLORS.length];
}

const locales = { 'es': esES };
const localizer = dateFnsLocalizer({
  format, parse, startOfWeek, getDay, locales,
});

// Formatea fecha para input type="date".
// Para fechaFin: el backend almacena fechaFin+1 día (para rango inclusivo en el calendario),
// por lo que restamos 1 día al mostrarlo en el formulario de edición.
const formatDateForInput = (dateString, esFechaFin = false) => {
  const date = new Date(dateString);
  if (esFechaFin) date.setDate(date.getDate() - 1);
  return format(date, 'yyyy-MM-dd');
};

const CalendarioAsignacionReferencia = () => {
  const [events, setEvents] = useState([]);
  const [maquinas, setMaquinas] = useState([]);
  const [referencias, setReferencias] = useState([]);
  const [maquinasDisponibles, setMaquinasDisponibles] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    machineNumber: '',
    referencia: '',
    fechaInicio: '',
    fechaFin: ''
  });
  const [editingEvent, setEditingEvent] = useState(null);
  const [error, setError] = useState(null);
  const [date, setDate] = useState(new Date());
  // Datos crudos para consultas en el formulario
  const [rawAsignaciones, setRawAsignaciones] = useState([]);
  const [rawMantenimientos, setRawMantenimientos] = useState([]);

  // Cargar datos iniciales
  const fetchInitialData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const [machinesRes, referenciasRes, asignacionesRes, mantenimientosRes, tiposMaquinaRes] = await Promise.all([
        fetch('/api/machines', { headers }),
        fetch('/api/referencias', { headers }),
        fetch('/api/asignar-referencia', { headers }),
        fetch('/api/mantenimiento', { headers }),
        fetch('/api/tipos-maquina', { headers })
      ]);
      const [machinesData, referenciasData, asignacionesData, mantenimientosData, tiposMaquinaData] = await Promise.all([
        machinesRes.json(),
        referenciasRes.json(),
        asignacionesRes.json(),
        mantenimientosRes.json(),
        tiposMaquinaRes.json()
      ]);
      
      // Crear un mapa de machineNumber -> tipoMaquina
      const tiposMaquinaMap = {};
      if (Array.isArray(tiposMaquinaData)) {
        tiposMaquinaData.forEach(tipo => {
          tiposMaquinaMap[tipo.machineNumber] = tipo.tipoMaquina;
        });
      }
      
      // Filtrar máquinas donde tipoMaquina sea "TEJEDURIA"
      const maquinasFiltradas = Array.isArray(machinesData) 
        ? machinesData.filter(m => {
            const tipoMaquina = tiposMaquinaMap[m.machineNumber];
            return tipoMaquina === 'TEJEDURIA';
          })
        : [];
      setMaquinas(maquinasFiltradas);
      setReferencias(Array.isArray(referenciasData) ? referenciasData : []);
      setRawAsignaciones(Array.isArray(asignacionesData) ? asignacionesData : []);
      setRawMantenimientos(Array.isArray(mantenimientosData) ? mantenimientosData : []);
      // Mapear asignaciones a eventos del calendario
      const mappedEvents = (Array.isArray(asignacionesData) ? asignacionesData : []).map(asig => {
        const inicio = new Date(asig.fechaInicio);
        const fin = new Date(asig.fechaFin);
        return {
          id: asig._id,
          title: `${asig.machineNumber} | ${asig.referencia}`,
          inicio,
          fin,
          raw: asig,
          color: getColorForReferencia(asig.referencia),
          tipo: 'asignacion'
        };
      });

      // Mapear mantenimientos como eventos bloqueados (excluir máquinas 9, 10, 11)
      const maquinasTeñido = ['9', '10', '11'];
      const mantenimientosEvents = (Array.isArray(mantenimientosData) ? mantenimientosData : [])
        .filter(mant => {
          const machineNum = String(mant.machineNumber).toLowerCase().replace(/^machine/, '');
          return !maquinasTeñido.includes(machineNum) && 
                 mant.estado !== 'cancelado';
        })
        .map(mant => {
          const inicio = new Date(mant.fechaInicio);
          const fin = new Date(mant.fechaFin);
          const horaInicio = format(inicio, 'HH:mm');
          const horaFin = format(fin, 'HH:mm');
          
          return {
            id: `mant-${mant._id}`,
            title: `🔧 MANTENIMIENTO M${mant.machineNumber} | ${mant.tipoMantenimiento.toUpperCase()} | ${horaInicio}-${horaFin}`,
            inicio,
            fin,
            raw: mant,
            color: '#dc2626', // Rojo para mantenimientos
            tipo: 'mantenimiento'
          };
        });

      setEvents([...mappedEvents, ...mantenimientosEvents]);
    } catch (err) {
      setError("No se pudieron cargar los datos iniciales.");
    }
  }, []);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  const handleSelectSlot = ({ start: inicio, end: fin }) => {
    const fechaInicio = format(inicio, 'yyyy-MM-dd');
    // Siempre restar un día a 'fin' para obtener el último día seleccionado
    const finDate = new Date(fin);
    finDate.setDate(finDate.getDate() - 1);
    const fechaFin = format(finDate, 'yyyy-MM-dd');

    setForm({
      machineNumber: '',
      referencia: '',
      fechaInicio,
      fechaFin
    });
    setEditingEvent(null);
    setMaquinasDisponibles([]); // Resetear máquinas disponibles
    setError(null);
    setOpen(true);
  };
  const handleSelectEvent = (event) => {
    // Si es un evento de mantenimiento, mostrar alerta y no abrir el diálogo
    if (event.tipo === 'mantenimiento') {
      setError('Este es un evento de mantenimiento. Debe editarse desde el Calendario de Mantenimiento.');
      setTimeout(() => setError(null), 5000);
      return;
    }

    setForm({
      machineNumber: event.raw.machineNumber,
      referencia: event.raw.referencia,
      fechaInicio: formatDateForInput(event.raw.fechaInicio),
      fechaFin: formatDateForInput(event.raw.fechaFin, true)  // revertir el +1 día almacenado
    });
    setEditingEvent(event);
    
    // Cargar máquinas disponibles para la referencia en edición
    const referenciaSeleccionada = referencias.find(r => r.referencia === event.raw.referencia);
    if (referenciaSeleccionada && referenciaSeleccionada.maquinas && referenciaSeleccionada.maquinas.length > 0) {
      const maquinasPermitidas = referenciaSeleccionada.maquinas.map(m => String(m.machineNumber));
      const maquinasFiltradas = maquinas.filter(m => maquinasPermitidas.includes(String(m.machineNumber)));
      setMaquinasDisponibles(maquinasFiltradas);
    } else {
      setMaquinasDisponibles([]);
    }
    
    setError(null);
    setOpen(true);
  };

  const handleChange = (field, value) => {
    setForm({ ...form, [field]: value });
    
    // Si se cambia la referencia, filtrar máquinas disponibles y resetear máquina seleccionada
    if (field === 'referencia') {
      const referenciaSeleccionada = referencias.find(r => r.referencia === value);
      if (referenciaSeleccionada && referenciaSeleccionada.maquinas && referenciaSeleccionada.maquinas.length > 0) {
        // Filtrar máquinas que estén en el array de maquinas de la referencia
        const maquinasPermitidas = referenciaSeleccionada.maquinas.map(m => String(m.machineNumber));
        const maquinasFiltradas = maquinas.filter(m => maquinasPermitidas.includes(String(m.machineNumber)));
        setMaquinasDisponibles(maquinasFiltradas);
      } else {
        setMaquinasDisponibles([]);
      }
      // Resetear máquina seleccionada cuando cambia la referencia
      setForm(prev => ({ ...prev, machineNumber: '' }));
    }
  };

  const handleBuscarFecha = (fecha) => {
    setDate(fecha);
  };

  /** Devuelve los períodos ocupados (asignaciones + mantenimientos) de una máquina,
   *  excluyendo el evento que se está editando actualmente. */
  const getPeriodosOcupados = (machineNumber) => {
    if (!machineNumber) return [];
    const maqStr = String(machineNumber);
    const ocupados = [];

    // Asignaciones existentes
    rawAsignaciones.forEach(a => {
      if (String(a.machineNumber) !== maqStr) return;
      if (editingEvent && a._id === editingEvent.id) return; // no mostrar la propia
      ocupados.push({
        inicio: new Date(a.fechaInicio),
        fin:    new Date(a.fechaFin),
        tipo:   'asignacion',
        detalle: a.referencia
      });
    });

    // Mantenimientos
    rawMantenimientos.forEach(m => {
      if (String(m.machineNumber) !== maqStr) return;
      if (m.estado === 'cancelado') return;
      ocupados.push({
        inicio: new Date(m.fechaInicio),
        fin:    new Date(m.fechaFin),
        tipo:   'mantenimiento',
        detalle: m.tipoMantenimiento
      });
    });

    return ocupados.sort((a, b) => a.inicio - b.inicio);
  };

  /** Cuenta asignaciones activas de una máquina. */
  const contarAsignaciones = (machineNumber) =>
    rawAsignaciones.filter(a => String(a.machineNumber) === String(machineNumber)).length;

  /** Verifica si el rango del formulario solapa con algún período ocupado de la máquina. */
  const detectarSolapamientoAsignacion = () => {
    if (!form.machineNumber || !form.fechaInicio || !form.fechaFin) return [];
    const fi = new Date(form.fechaInicio);
    const ff = new Date(form.fechaFin);
    return getPeriodosOcupados(form.machineNumber).filter(p => fi <= p.fin && ff >= p.inicio);
  };

  const handleSave = async () => {
    setError(null);
    if (!form.machineNumber || !form.referencia || !form.fechaInicio || !form.fechaFin) {
      setError("Completa todos los campos.");
      return;
    }

    // Validar que la fecha de inicio no sea mayor que la fecha de fin
    const fechaInicio = new Date(form.fechaInicio);
    const fechaFin = new Date(form.fechaFin);
    
    if (fechaInicio > fechaFin) {
      setError("La fecha de inicio no puede ser mayor que la fecha de fin.");
      return;
    }

    // Validar que no haya conflicto con mantenimientos ni asignaciones existentes
    const conflictos = detectarSolapamientoAsignacion();
    if (conflictos.length > 0) {
      const detalles = conflictos.map(c => {
        const ini = format(c.inicio, 'dd/MM/yyyy');
        const fin = format(c.fin,   'dd/MM/yyyy');
        const etiqueta = c.tipo === 'mantenimiento'
          ? `🔧 MANTENIMIENTO (${c.detalle.toUpperCase()})`
          : `📌 ASIGNACIÓN (${c.detalle})`;
        return `${etiqueta} del ${ini} al ${fin}`;
      }).join(' | ');
      setError(`Conflicto de fechas con: ${detalles}. Por favor, selecciona otras fechas.`);
      return;
    }

    const datos = {
      machineNumber: form.machineNumber,
      referencia: form.referencia,
      fechaInicio: form.fechaInicio,
      fechaFin: form.fechaFin
    };

    const token = localStorage.getItem('token');
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };

    try {
      let response;
      if (editingEvent) {
        response = await fetch(`/api/asignar-referencia/${editingEvent.id}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(datos)
        });
      } else {
        response = await fetch('/api/asignar-referencia', {
          method: 'POST',
          headers,
          body: JSON.stringify(datos)
        });
      }

      if (response.ok) {
        setOpen(false);
        fetchInitialData();
      } else {
        const errorData = await response.json();
        setError(`Error al guardar: ${errorData.message || 'Error desconocido'}`);
      }
    } catch (err) {
      setError("Error de conexión al servidor.");
    }
  };

  const handleDelete = async () => {
    setError(null);
    if (!editingEvent) return;

    const token = localStorage.getItem('token');
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

    try {
      const response = await fetch(`/api/asignar-referencia/${editingEvent.id}`, {
        method: 'DELETE',
        headers
      });
      if (response.ok) {
        setOpen(false);
        fetchInitialData();
      } else {
        const errorData = await response.json();
        setError(`Error al eliminar: ${errorData.message || 'Error desconocido'}`);
      }
    } catch (err) {
      setError("Error de conexión al servidor al eliminar.");
    }
  };

  return (
    <div style={{ height: 900, margin: 20 }}>
      {/* Título del calendario */}
      <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 12, textAlign: 'center' }}>
        Asignacion Referencia
      </Typography>
      
      {/* Alerta global para errores de selección */}
      {error && !open && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {/* Componente de búsqueda de fecha */}
      <div style={{ marginBottom: 20 }}>
        <TextField
          label="Buscar fecha"
          type="date"
          onChange={(e) => handleBuscarFecha(new Date(e.target.value))}
          InputLabelProps={{ shrink: true }}
        />
      </div>
      <Calendar
  localizer={localizer}
  culture='es'
  events={events}
  startAccessor="inicio"
  endAccessor="fin"
  selectable
  onSelectSlot={handleSelectSlot}
  onSelectEvent={handleSelectEvent}
  views={['month', 'day', 'agenda']}
  style={{ background: 'rgb(69, 69, 71)', borderRadius: 8 }}
  date={date}
  onNavigate={(newDate) => setDate(newDate)}
  eventPropGetter={event => {
    return {
      className: 'evento-referencia',
      style: {
        backgroundColor: event.color,
        color: '#fff',
        borderRadius: '6px',
        border: 'none'
      }
    };
  }}
        messages={{
          date: 'Fecha',
          time: 'Hora',
          event: 'Evento',
          allDay: 'Todo el día',
          week: 'Semana',
          work_week: 'Semana laboral',
          day: 'Día',
          month: 'Mes',
          previous: 'Anterior',
          next: 'Siguiente',
          yesterday: 'Ayer',
          tomorrow: 'Mañana',
          today: 'Hoy',
          agenda: 'Agenda',
          showMore: total => `+${total} más`,
          noEventsInRange: 'No hay eventos en este rango',
          back: 'Atrás'
        }}
      />

      <Dialog open={open} onClose={() => setOpen(false)} container={document.body}>
        <DialogTitle>{editingEvent ? 'Editar asignación' : 'Nueva asignación'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TextField
                  select
                  label="Referencia"
                  value={form.referencia}
                  onChange={e => handleChange('referencia', e.target.value)}
                  fullWidth
                  required
                  MenuProps={{ disablePortal: true }}
                >
                  <MenuItem value=""><em>Seleccione una referencia</em></MenuItem>
                  {referencias.map(r => (
                    <MenuItem key={r._id} value={r.referencia}>{r.referencia}</MenuItem>
                  ))}
                </TextField>
                {form.referencia && (
                  <Box sx={{
                    width: 24, height: 24, ml: 2,
                    backgroundColor: getColorForReferencia(form.referencia),
                    borderRadius: '4px', border: '1px solid #ccc'
                  }} />
                )}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                label="Máquina"
                value={form.machineNumber}
                onChange={e => handleChange('machineNumber', e.target.value)}
                fullWidth
                required
                disabled={!form.referencia}
                MenuProps={{ disablePortal: true }}
              >
                <MenuItem value=""><em>{form.referencia ? 'Seleccione una máquina' : 'Primero seleccione una referencia'}</em></MenuItem>
                {maquinasDisponibles.map(m => {
                  const cnt = contarAsignaciones(m.machineNumber);
                  return (
                    <MenuItem key={m._id} value={m.machineNumber}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                        <span>{m.machineNumber}</span>
                        <Chip
                          label={`${cnt} asig.`}
                          size="small"
                          color={cnt === 0 ? 'success' : cnt < 5 ? 'warning' : 'error'}
                          sx={{ ml: 1, fontSize: '0.7rem' }}
                        />
                      </Box>
                    </MenuItem>
                  );
                })}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Fecha de inicio"
                type="date"
                value={form.fechaInicio}
                onChange={e => handleChange('fechaInicio', e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Fecha de fin"
                type="date"
                value={form.fechaFin}
                onChange={e => handleChange('fechaFin', e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>

            {/* Panel de períodos ocupados de la máquina seleccionada */}
            {form.machineNumber && (() => {
              const ocupados = getPeriodosOcupados(form.machineNumber);
              const conflictos = detectarSolapamientoAsignacion();
              return (
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                    Períodos ocupados — {form.machineNumber} ({ocupados.length} total)
                  </Typography>
                  {ocupados.length === 0 ? (
                    <Chip label="Sin ocupaciones" color="success" size="small" />
                  ) : (
                    <Box sx={{ maxHeight: 140, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {ocupados.map((p, i) => {
                        const ini = format(p.inicio, 'dd/MM/yyyy');
                        const fin = format(p.fin,   'dd/MM/yyyy');
                        const esConflicto = conflictos.some(
                          c => c.inicio.getTime() === p.inicio.getTime() && c.fin.getTime() === p.fin.getTime()
                        );
                        return (
                          <Tooltip key={i} title={p.tipo === 'mantenimiento' ? `Mantenimiento: ${p.detalle}` : `Referencia: ${p.detalle}`}>
                            <Chip
                              label={`${p.tipo === 'mantenimiento' ? '🔧' : '📌'} ${ini} → ${fin}  ${p.detalle}`}
                              size="small"
                              color={esConflicto ? 'error' : p.tipo === 'mantenimiento' ? 'warning' : 'default'}
                              variant={esConflicto ? 'filled' : 'outlined'}
                              sx={{ fontSize: '0.7rem', justifyContent: 'flex-start' }}
                            />
                          </Tooltip>
                        );
                      })}
                    </Box>
                  )}
                </Grid>
              );
            })()}
          </Grid>
        </DialogContent>
        <DialogActions>
          {editingEvent && (
            <Button color="error" onClick={handleDelete}>Eliminar</Button>
          )}
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CalendarioAsignacionReferencia;